import { TravelMode } from "./TimeTracking";
import { Role } from "./Role";
import { Licence } from "@/data/models/Licence";
import { RecordInfo } from "@/data/models/RecordInfo";
import { Language } from "@/i18n/Languages";
import { UserModule } from "@/store/modules/UserModule";
import { getIgnoreIresAdminOrIresClientCheck } from "../repository/ClientRepository";
import { firestore } from "firebase";
import { ProjectImportSource as UserImportSource } from "./ProjectDetails";
import { GeoLocation } from "@/utils/GeocodingService";
import { MiraSavedComparison } from "@/data/models/MiraSavedComparison";

export enum ValidationLevel {
  LOW = "LOW",
  NORMAL = "NORMAL",
  STRICT = "STRICT",
  NOT_SPECIFIED = "NOT_SPECIFIED",
}

/**
 * Enum defining user roles.
 */
// `Role` lives in a dependency-free leaf module so that consumers which only need the enum do not
// pull this file (and its ClientRepository import cycle) in with it. Re-exported for compatibility.
export { Role } from "./Role";

export const IRES_ADMIN_ACCOUNT = "IRES_ADMIN_ACCOUNT_ID";
export const WEB_ADMIN_ID = "WEB_ADMIN_ID";
export const MASTER_ID = "Master";

interface LicenceInfo {
  sessionToken?: string;
  portalSessionToken?: string;
  licences: Array<Licence>;
  licencesV3?: Array<Licence>;
}

export function getLatestLicences(licenceInfo?: LicenceInfo | null) {
  return licenceInfo?.licencesV3 ?? licenceInfo?.licences;
}

export enum UserOperationName {
  USER_BLOCKED = "USER_BLOCKED",
  USER_CREATED = "USER_CREATED",
  USER_DELETED = "USER_DELETED",
  USER_EDITED = "USER_EDITED",
  USER_RESTORED = "USER_RESTORED",
  USER_UNBLOCKED = "USER_UNBLOCKED",
}

export enum UserOperationSource {
  API = "API",
  PORTAL = "PORTAL",
  PROJECT_MANAGER_APP = "PROJECT_MANAGER_APP",
  DEVICE_MANAGER_APP = "DEVICE_MANAGER_APP",
  CLOUD_FUNCTION = "CLOUD_FUNCTION",
  ERP = "ERP",
  PYTHON_SCRIPT = "PYTHON_SCRIPT",
}


export interface UserLastOperation {
  name: UserOperationName;
  timestamp: firestore.Timestamp;
  recordedByUserId: string;
  source?: UserOperationSource;
}

interface User {
  id: string;
  nickname: string;
  staffNumber?: string;
  supervisorId?: string;
  firstName?: string;
  lastName?: string;
  role?: Role;
  hasEnabledCockpit?: boolean;
  validationLevel?: ValidationLevel;
  passwordHash?: string;
  passwordHashBase64?: string;
  passwordHashHex?: string;
  salt?: string;
  hasTemporaryPassword?: boolean | null;
  emailAddress?: string;
  mobilePhone?: string;
  deleted?: boolean;
  blocked?: boolean;
  licenceInfo?: LicenceInfo | null;
  branches?: string[];
  sharedEmailAddressId?: string | null;
  wasSetToAdmin?: boolean;
  usesTablet?: boolean;
  stationaryPhone?: string;
  postalCode?: string;
  location?: string;
  addressStreet?: string;
  addressHousenumber?: string;
  country?: string;
  /**
   * Position of the user's address: geocoded automatically by IRES Cloud on write, or placed by hand on
   * the map, which is recorded as quality `MANUAL`.
   *
   * A hand-placed pin survives every write that leaves the address alone. Since 2026-08-18 an **edited
   * address replaces it**, with the confidence the new lookup earns: a pin describes the address it was
   * placed for. See `docs/components/location-picker.md`.
   */
  coordinates?: GeoLocation | null;
  /**
   * What IRES Work's travel planner does with this user's position: their device's GPS, a simulated
   * drive, or nothing (FAHRT). Absent means NAVIGATION.
   *
   * **No longer editable in IRES Office.** Hans asked for the picker on 2026-09-13 ("we do not need
   * this anymore") and it was removed from `UserFormDialog` on 2026-09-14, so this app neither reads
   * nor writes the field. It stays declared because the value is still in Firestore and IRES Work
   * still reads it (`TravelSettingsFromOffice.resolveTravelSettings`), where anything absent or
   * unrecognised resolves to NAVIGATION.
   *
   * Typed tolerantly for the same reason `endReason` is: IRES Work may learn a value this app does
   * not know yet, and a strict union would make such a user fail to read.
   */
  travelMode?: TravelMode | string | null;
  vehicle?: string;
  vehicleAtHome?: boolean;
  lastEdit?: string;
  lastChangedBy?: RecordInfo;
  language?: Language;
  /** Per-user dark-mode preference; follows the user across devices/browsers. */
  darkMode?: boolean;
  /**
   * The figures the user chose for each Mira landing card (#2610102): metric ids in the order they
   * were picked, keyed by `MiraSection`. A section without an entry shows its screen's first figures.
   */
  miraLandingFigures?: Record<string, string[]>;
  /**
   * The user's saved Mira comparisons downwards (#2610137 tasks 22 / 24), keyed by the client id they were
   * made in: a branch id or a member id means something in one client only. Visible to this user only.
   */
  miraComparisons?: Record<string, MiraSavedComparison[]>;
  userSourceId?: string;
  lastOperation?: UserLastOperation;
  userImportSource?: UserImportSource;
  homeClient?: string;
  clients?: string[];
  sharedUserOfGroup?: boolean;
}

export function isIresAdminAccount(user: User | null, skipIgnoreIresAdminOrIresClientCheck?: boolean) {
  if (!user) {
    return false;
  }

  if (!skipIgnoreIresAdminOrIresClientCheck && getIgnoreIresAdminOrIresClientCheck()) {
    return false;
  }

  return user.id == IRES_ADMIN_ACCOUNT;
}

export function isAdmin(user: User | null) {
  if (!user) {
    return false;
  }

  return user.wasSetToAdmin
    ? user.wasSetToAdmin === true
    : isIresAdminAccount(user);
}

/**
 * The user's own Admin flag, as the user dialog shows and saves it: the stored `wasSetToAdmin`,
 * else the IRES admin account. This is what IRES Cloud, IRES Project and IRES Work read.
 *
 * Use it wherever another user's admin status is *displayed*. `adminRightsAccess()` is not that:
 * on an IRES-type client the IRES special rights grant it to every user, so it says "Ja" for all
 * (ticket 2610031). `isAdmin()` is not either: it ignores a stored `false` on the IRES admin
 * account, which may switch its own flag off.
 */
export function getAdminFlag(user: User | null) {
  if (!user) {
    return false;
  }

  return user.wasSetToAdmin ?? isIresAdminAccount(user);
}

export function isSysAdmin(user: User | null) {
  if (!user) {
    return false;
  }

  return user.role === Role.ADMIN;
}

export function isDeviceManager(user: User | null) {
  if (!user) {
    return false;
  }

  return user.role === Role.CUSTOM_ROLE_5;
}

export function doesUserBelongToBranch(
  user: User, 
  branchId: string,
  strict = false
) {
  if (strict) {
    if (!user.branches || user.branches.length === 0) {
      return false;
    }

    return user.branches.some((userBranchId) => userBranchId == branchId);
  }

  return user.branches
    ? user.branches.length === 0 ||
        user.branches.some((userBranchId) => userBranchId == branchId)
    : true;
}

/**
 * Picks the first user the {@link matches} predicate accepts, preferring one that is not deleted.
 *
 * User identifiers are not unique. `staffNumber` in particular is free text, and clients do share a
 * single placeholder number across their whole staff (BELFOR UK has 21 users on `12345677`), so a
 * lookup by identifier can match many users. A deleted account must never win such a lookup over an
 * active one, or the app credits work to someone who has left the company.
 */
export function pickUserPreferringActive(
  users: Array<User>,
  matches: (user: User) => boolean
): User | undefined {
  return (
    users.find((user) => matches(user) && !user.deleted) ?? users.find(matches)
  );
}

export default User;

export function countUserWithRole(thisRole: Role) {
  const user = UserModule.users;
  const userWithRole = user.filter(
    (user) =>
      user.role === thisRole &&
      !user.deleted &&
      user.id !== "IRES_ADMIN_ACCOUNT_ID"
  );
  return userWithRole.length;
}

export function getFullName(user: User) {
  const firstName = user.firstName?.trim();
  const lastName = user.lastName?.trim();

  if (
    firstName !== "" &&
    lastName !== ""
  ) {
    return `${firstName} ${lastName}`;
  }
  else if (firstName !== "") {
    return firstName;
  }
  else if (lastName !== "") {
    return lastName;
  }

  return user.nickname;
}

export function getDisplayName(user: User) {
  const fullName = getFullName(user);
  const nickname = user.nickname;

  if (fullName === nickname) {
    return nickname;
  }

  return `${nickname} (${fullName})`;
}